import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { createGlobalStyle } from 'styled-components';

import { getDefaultErrorColor, getDefaultTextColor, getSystemBackgroundColor } from '../lib/colorPicker';
import { IUEventColors } from '../lib/event';
import { IUUserContext, useUserContext } from './UserContext';

import { WHITE } from 'Common/src/lib/Colors';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';

import { TAppElkBackgroundAnimation } from 'TProtocol/prototypes/events/messages';

interface IUSystemColors {
  buttonText: string;
  error: string;
  background: string;
  backgroundText: string;
}

interface IUBackgroundContext {
  colors: IUEventColors;
  systemColors: IUSystemColors;
  animation?: TAppElkBackgroundAnimation;
  setScheme: ({ scheme, animation }: { scheme: ColorScheme, animation?: TAppElkBackgroundAnimation }) => void;
  setColors: ({ colors, animation }: { colors?: IUEventColors; animation?: TAppElkBackgroundAnimation }) => void;
}

export enum ColorScheme {
  None,
  Yellow,
  Orange,
  Red,
  Purple,
  Violet,
  Blue,
  Indigo,
  Green,
  Home,
  Welcome
}

interface SchemeColors {
  primaryBackground: string;
  secondaryBackground: string;
  textColor: string;
}

export const COLOR_SCHEMES: {
  [key in ColorScheme]: SchemeColors;
} = {
  [ColorScheme.None]: {
    primaryBackground: '',
    secondaryBackground: '',
    textColor: ''
  },
  [ColorScheme.Yellow]: {
    primaryBackground: '#FFCB02',
    secondaryBackground: '#EF6623',
    textColor: ''
  },
  [ColorScheme.Orange]: {
    primaryBackground: '#EF6623',
    secondaryBackground: '#ED1B2E',
    textColor: ''
  },
  [ColorScheme.Red]: {
    primaryBackground: '#ED1B2E',
    secondaryBackground: '#A3238E',
    textColor: ''
  },
  [ColorScheme.Purple]: {
    primaryBackground: '#A3238E',
    secondaryBackground: '#7A469B',
    textColor: ''
  },
  [ColorScheme.Violet]: {
    primaryBackground: '#7A469B',
    secondaryBackground: '#0359AA',
    textColor: ''
  },
  [ColorScheme.Blue]: {
    primaryBackground: '#0359AA',
    secondaryBackground: '#02ABDD',
    textColor: ''
  },
  [ColorScheme.Indigo]: {
    primaryBackground: '#02ABDD',
    secondaryBackground: '#4BB749',
    textColor: ''
  },
  [ColorScheme.Green]: {
    primaryBackground: '#4BB749',
    secondaryBackground: '#FFCB02',
    textColor: ''
  },
  [ColorScheme.Home]: {
    primaryBackground: '#FFDF64',
    secondaryBackground: '#EF6623',
    textColor: ''
  },
  [ColorScheme.Welcome]: {
    primaryBackground: '#ffffff',
    secondaryBackground: '#FFCB02',
    textColor: '#FC4C10',
  },

};

if (window.CSS.registerProperty) {
  window.CSS.registerProperty({
    name: '--x',
    syntax: '<percentage>',
    inherits: false,
    initialValue: '0%',
  });
  window.CSS.registerProperty({
    name: '--y',
    syntax: '<percentage>',
    inherits: false,
    initialValue: '100%',
  });
  window.CSS.registerProperty({
    name: '--shine-bg-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#FFF',
  });
  window.CSS.registerProperty({
    name: '--shine-secondary-bg-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#FFF',
  });
  window.CSS.registerProperty({
    name: '--shine-text-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#FFF',
  });
  window.CSS.registerProperty({
    name: '--shine-highlight-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#FFF',
  });
  window.CSS.registerProperty({
    name: '--shine-system-button-text-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#000',
  });
  window.CSS.registerProperty({
    name: '--shine-background-text-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#000',
  });
  window.CSS.registerProperty({
    name: '--shine-system-error-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#000',
  });
  window.CSS.registerProperty({
    name: '--shine-system-background-color',
    syntax: '<color>',
    inherits: true,
    initialValue: 'rgb(0, 0, 0, 0.5)',
  });
  window.CSS.registerProperty({
    name: '--shine-default-background-color',
    syntax: '<color>',
    inherits: true,
    initialValue: '#fff',
  });
}

export default COLOR_SCHEMES


export const DEFAULT_COLORS: IUEventColors = {
  primary: '#ffffff',
  secondary: '#FFCB02',
  highlightText: '#FC4C10',
  text: '#000000',
  buttonTextColor: '#ffffff'
};

export const DEFAULT_SYSTEM_COLORS: IUSystemColors = {
  buttonText: getDefaultTextColor(DEFAULT_COLORS.highlightText),
  error: getDefaultErrorColor(DEFAULT_COLORS),
  background: getSystemBackgroundColor(DEFAULT_COLORS),
  backgroundText: getDefaultTextColor(DEFAULT_COLORS.primary)
}

const getInitialColors = (userContext: IUUserContext) => {
  const colors = { ...DEFAULT_COLORS };

  try {
    const params = new URLSearchParams(window.location.search);
    const c = params.get('ec');
    if (c) {
      colors.primary = atob(c);
      colors.secondary = atob(c);
    }
    const tc = params.get('tc');
    if (tc) {
      colors.text = atob(tc);
    }
  } catch (e) {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.WARN,
      userId: userContext.id,
      tag: ULogTag.General,
      message: `[BackgroundContext] Error decoding default colors: ${JSON.stringify(e)}`
    });
  }

  return colors;
};

const BackgroundContext = createContext<IUBackgroundContext | null>(null);

export const BackgroundContextProvider = (props: PropsWithChildren<object>) => {
  const userContext = useUserContext();

  const [colors, setColors] = useState<IUEventColors>(getInitialColors(userContext));
  const [systemColors, setSystemColors] = useState<IUSystemColors>(DEFAULT_SYSTEM_COLORS);
  const [animation, setAnimation] = useState<TAppElkBackgroundAnimation | undefined>();


  const setScheme = ({ scheme, animation }: { scheme: ColorScheme, animation?: TAppElkBackgroundAnimation }) => {
    setAnimation(animation);

    const colorScheme = COLOR_SCHEMES[scheme];
    if (colorScheme.primaryBackground === '#350D8C') {
      setColors({
        primary: colorScheme.primaryBackground,
        secondary: colorScheme.secondaryBackground,
        highlightText: '#9747FF',
        text: '#FC4C10',
        buttonTextColor: WHITE
      });
    } else {
      setColors({
        primary: colorScheme.primaryBackground,
        secondary: colorScheme.secondaryBackground,
        highlightText: '#FC4C10',
        text: "#000000",
        buttonTextColor: colorScheme.primaryBackground
      });
    }


  };


  const setColorScheme = ({ colors, animation }: { colors?: IUEventColors, animation?: TAppElkBackgroundAnimation }) => {
    setAnimation(animation);
    setColors(colors ?? DEFAULT_COLORS);
  }

  useEffect(() => {
    const systemButtonText = getDefaultTextColor(colors.highlightText);
    const systemBackgroundText = getDefaultTextColor(colors.primary);
    const systemError = getDefaultErrorColor(colors);
    const systemBackground = getSystemBackgroundColor(colors);

    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', colors.primary);

    document.body.style.setProperty('--x', '0%');
    document.body.style.setProperty('--y', '100%');
    document.body.style.setProperty('--shine-bg-color', colors.primary);
    document.body.style.setProperty('--shine-secondary-bg-color', colors.secondary);
    document.body.style.setProperty('--shine-text-color', colors.text);
    document.body.style.setProperty('--shine-highlight-color', colors.highlightText);
    document.body.style.setProperty('--shine-background-text-color', systemBackgroundText);
    document.body.style.setProperty('--shine-system-button-text-color', systemButtonText);
    document.body.style.setProperty('--shine-system-error-color', systemError);
    document.body.style.setProperty('--shine-system-background-color', systemBackground);
    document.body.style.setProperty('--shine-default-background-color', DEFAULT_COLORS.primary);

    setSystemColors({
      buttonText: systemButtonText,
      error: systemError,
      background: systemBackground,
      backgroundText: systemBackgroundText
    });

  }, [colors]);

  const context: IUBackgroundContext = {
    animation,
    colors,
    systemColors,
    setScheme,
    setColors: setColorScheme
  };

  return <BackgroundContext.Provider value={context}>
    <GlobalStyle/>
    {props.children}
  </BackgroundContext.Provider>;
};

const GlobalStyle = createGlobalStyle`
  @keyframes colorChange {

  }

  body {
    transition: --shine-bg-color 1s, --shine-secondary-bg-color 1s, --shine-text-color 1s, --shine-background-text-color 1s;
    color: var(--shine-text-color);
  }
`;

export const useBackgroundContext = () => {
  const context = useContext(BackgroundContext);
  if (context === null) {
    throw new Error('useBackgroundContext must be used within a BackgroundContextProvider');
  }
  return context;
};
